// Lightweight platform checks (regex over navigator.userAgent — no ua-parser) so the login
// first-paint bundle stays free of ua-parser-js (~72 kB). utils/device re-exports these for its
// existing callers; the only genuinely ua-parser-backed helpers (getBrowserName/getOSName) stay
// in utils/device. Keeping isGecko + getLinearAccelerationMagnitude here too means the eager
// first-paint code (src/index.js, utils/backTrap) never pulls ua-parser into the main chunk.

const ua = () => (typeof navigator !== 'undefined' && navigator.userAgent) || '';

export const isAndroid = () => /Android/i.test(ua());

// iPadOS 13+ presents a desktop Safari UA; the touch-points probe catches that case.
export const isIos = () =>
  /iP(hone|ad|od)/i.test(ua()) ||
  (typeof navigator !== 'undefined' &&
    navigator.platform === 'MacIntel' &&
    navigator.maxTouchPoints > 1);

export const isMobile = () => isAndroid() || isIos();
export const isDesktop = () => !isMobile();

// True when running as an installed PWA (its own window, not a browser tab): a standalone /
// fullscreen / minimal-ui display mode, or iOS' navigator.standalone. In a PWA the Back-trap is
// skipped so the browser history stays a single entry — which both lets a panic close the window
// outright and means the native Back just closes the app (see utils/backTrap, utils/redirect).
// Takes an explicit `win` for testability.
export const isStandaloneDisplay = (win = typeof window !== 'undefined' ? window : undefined) =>
  !!win &&
  (win.navigator?.standalone === true ||
    ['standalone', 'fullscreen', 'minimal-ui'].some(
      (m) => win.matchMedia?.(`(display-mode: ${m})`)?.matches
    ));

// True when this document runs in an opaque origin — e.g. a `sandbox`ed iframe without
// `allow-same-origin`, where `self.origin` serialises to the literal string "null". The login
// frame uses this to tell the single-origin sandbox build (talk to the app by held window
// reference, since neither side's origin can be trusted/named) from a real cross-origin subdomain.
// Takes an explicit `win` for testability.
export const isOpaqueOrigin = (win = typeof window !== 'undefined' ? window : undefined) =>
  !!win && win.origin === 'null';

// Gecko engine (Firefox desktop + Android), which applies the stricter Back-button intervention
// the back-trap works around (see utils/backTrap). Real Gecko browsers carry a `Gecko/<digits>`
// token ("Gecko/20100101 Firefox/…" on desktop, "Gecko/124.0 Firefox/…" on Android); WebKit/Blink
// UAs only ever say "like Gecko", and Firefox on iOS (FxiOS) is WebKit — all correctly excluded by
// requiring a digit after the slash. An explicit `ua` keeps callers testable; omit it for the real
// navigator. Replaces the former ua-parser engine check to keep ua-parser off the eager path.
export const isGecko = (userAgent = ua()) => /Gecko\/\d/.test(userAgent);

export const GRAVITY = 9.81; // standard gravity, m/s²

const hasAllAxes = (vec) => vec && vec.x != null && vec.y != null && vec.z != null;
const vectorMagnitude = ({ x, y, z }) => Math.sqrt(x ** 2 + y ** 2 + z ** 2);

// Raw magnitudes (m/s²) of both `devicemotion` channels: `linear` is the gravity-free
// `acceleration`, `total` is `accelerationIncludingGravity` (≈ g at rest, ≈ 0 in freefall).
// Either is null when the device doesn't report that channel.
export const getMotionReadings = (event) => ({
  linear: hasAllAxes(event.acceleration) ? vectorMagnitude(event.acceleration) : null,
  total: hasAllAxes(event.accelerationIncludingGravity)
    ? vectorMagnitude(event.accelerationIncludingGravity)
    : null,
});

// Single linear-acceleration estimate from a `devicemotion` event. Prefers the gravity-free
// channel; falls back to the total magnitude's deviation from rest (|g|) as a proportional
// proxy — `Math.abs` deliberately counts both acceleration and deceleration/free-fall. Note
// the proxy underestimates jerks not aligned with gravity (~2x when horizontal), so consumers
// comparing it against a threshold should use a lower bar on the fallback channel.
// Returns null when no usable data is present.
export const getLinearAccelerationMagnitude = (event) => {
  const { linear, total } = getMotionReadings(event);
  if (linear != null) return linear;
  if (total != null) return Math.abs(total - GRAVITY);
  return null;
};
